/**
 * Forgot Password Page - PHSIMS
 * 
 * Public page for users to request password reset email.
 * Matches the PHSIMS login page design exactly.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as BackIcon,
  Send as SendIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { AppFooter } from './common/AppFooter';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch {
      // Still show success to prevent email enumeration
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  // Validate email format
  const isValidEmail = (emailValue: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        // Same deep forest green background as LoginPage
        background: '#0d3322',
        position: 'fixed',
        top: 0,
        left: 0,
        p: { xs: 2, sm: 0 },
      }}
    >
      {/* White Card - Identical styling to LoginPage */}
      <Box
        sx={{
          background: 'white',
          padding: { xs: '24px 20px', sm: '32px 40px' },
          borderRadius: { xs: '16px', sm: '20px' },
          boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
          width: { xs: '100%', sm: 460 },
          maxWidth: { xs: '100%', sm: '94%' },
          position: 'relative',
          zIndex: 1,
          // Orange top ribbon
          borderTop: '5px solid #ef7d00',
        }}
      >
        {/* Header Section - Identical to LoginPage */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2.5, sm: 3 } }}>
          {/* Logo */}
          <Box
            component="img"
            src="/znphi_logo.jpg"
            alt="ZNPHI Logo"
            sx={{
              width: { xs: 70, sm: 90 },
              height: 'auto',
              borderRadius: '8px',
              mb: 1.5,
              objectFit: 'contain',
            }}
          />
          
          {/* System Name */}
          <Typography
            sx={{
              color: '#0d5c2e',
              fontSize: { xs: '16px', sm: '20px' },
              fontWeight: 800,
              lineHeight: 1.3,
              mb: 0.5,
              letterSpacing: '0.2px',
            }}
          >
            Public Health Security &<br />Information Management System
          </Typography>
          
          {/* Page Title - Orange */}
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '22px', sm: '26px' },
              fontWeight: 800,
              letterSpacing: '1px',
              mt: 0.5,
              color: '#ef7d00',
            }}
          >
            Password Reset
          </Typography>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: '10px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              fontSize: '13px',
              py: 0.5,
            }}
          >
            {error}
          </Alert>
        )}

        {success ? (
          /* Success State */
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <SuccessIcon sx={{ fontSize: 56, color: '#198754', mb: 2 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 1, 
                color: '#0d5c2e',
                fontSize: { xs: '16px', sm: '18px' },
              }}
            >
              Check Your Email
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748b', 
                mb: 2,
                fontSize: { xs: '13px', sm: '14px' },
                px: 1,
              }}
            >
              If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#94a3b8', 
                mb: 3,
                fontSize: { xs: '11px', sm: '12px' },
                px: 1,
                fontStyle: 'italic',
              }}
            >
              Please also check your spam/junk folder. The link expires in 24 hours.
            </Typography>
            <Button
              variant="contained"
              startIcon={<BackIcon />}
              onClick={() => navigate('/login')}
              sx={{
                py: 1.2,
                px: 3,
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #198a4b 0%, #14703d 100%)',
                boxShadow: '0 4px 20px rgba(25, 138, 75, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ef7d00 0%, #f59e0b 100%)',
                  boxShadow: '0 8px 30px rgba(239, 125, 0, 0.4)',
                },
              }}
            >
              Back to Login
            </Button>
          </Box>
        ) : (
          /* Reset Form */
          <form onSubmit={handleSubmit}>
            <Typography
              sx={{
                color: '#64748b',
                fontSize: '13px',
                textAlign: 'center',
                mb: 2,
              }}
            >
              Enter your email address to receive a password reset link
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Email Field - Same styling as LoginPage inputs */}
              <TextField
                placeholder="Email address"
                type="email"
                variant="outlined"
                fullWidth
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#f8fafc',
                    fontWeight: 500,
                    minHeight: '48px',
                    '& fieldset': {
                      borderWidth: '2px',
                      borderColor: '#e2e8f0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#198a4b',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#198a4b',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                      boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 12px 12px 0',
                    fontSize: '14px',
                  },
                }}
              />

              {/* Submit Button - Same styling as LoginPage */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !email || !isValidEmail(email)}
                startIcon={!loading && <SendIcon sx={{ fontSize: 18 }} />}
                sx={{
                  mt: 0.5,
                  py: 1.2,
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #198a4b 0%, #14703d 100%)',
                  boxShadow: '0 4px 20px rgba(25, 138, 75, 0.35)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ef7d00 0%, #f59e0b 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 30px rgba(239, 125, 0, 0.4)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&:disabled': {
                    background: '#cbd5e1',
                    boxShadow: 'none',
                    transform: 'none',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              {/* Back to Login Link */}
              <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                <Typography
                  component="a"
                  href="/login"
                  sx={{
                    color: '#64748b',
                    fontSize: '13px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: '#ef7d00',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Back to login
                </Typography>
              </Box>
            </Box>
          </form>
        )}

        {/* Footer */}
        <AppFooter variant="auth" />
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
